import React, { useState, forwardRef, useEffect, useCallback } from 'react';
import { CounsellingInfoWithId } from '../../types/forms';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  FormControlLabel,
  Alert,
  Divider,
  CircularProgress,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as UploadFileIcon,
} from '@mui/icons-material';
import { uploadFile, deleteFile, refreshFileUrl } from '../../services/uploadService';
import { useParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { fetchPublicLookups } from '../../api/lookups';
import { Lookup as LookupType } from '../../types/lookup';
import AvailabilitySlotEditor, { AvailabilitySlot } from '../shared/AvailabilitySlotEditor';

interface CounsellingInformationTabProps {
  formData: CounsellingInfoWithId;
  onChange: (field: string, value: any) => void;
  onNext: () => void;
  onBack: () => void;
  onSave: () => void;
  saveStatus: 'saved' | 'error' | null;
}

const CounsellingInformationTab = forwardRef<HTMLDivElement, CounsellingInformationTabProps>((props, ref) => {
  const { formData, onChange, onNext, onBack, onSave, saveStatus } = props;
  const { token } = useParams<{ token: string }>();

  const [sessionTypes, setSessionTypes] = useState<LookupType[]>([]);
  const [modalities, setModalities] = useState<LookupType[]>([]);
  const [availabilities, setAvailabilities] = useState<LookupType[]>([]);
  const [experienceTrainingOptions, setExperienceTrainingOptions] = useState<LookupType[]>([]);
  const [languages, setLanguages] = useState<LookupType[]>([]);
  // Track if user has made a selection for qualification status
  const [qualificationSelected, setQualificationSelected] = useState(
    formData.qualified === true || formData.qualified === false
  );
  
  // Reset qualified to undefined if it's false to ensure no default selection
  const resetQualified = useCallback(() => {
    if (formData.qualified === false && !qualificationSelected) {
      // Only reset if the user hasn't explicitly made a selection
      onChange('qualified', undefined);
    }
  }, [formData.qualified, qualificationSelected, onChange]);

  useEffect(() => {
    resetQualified();
  }, [resetQualified]);

  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [cvUrl, setCvUrl] = useState<string | null>(null);
  
  // State to control dropdown open/close
  const [sessionTypesOpen, setSessionTypesOpen] = useState(false);
  const [modalityOpen, setModalityOpen] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [sessionTypeData, modalityData, availabilityData, experienceTrainingData, languageData]: [LookupType[], LookupType[], LookupType[], LookupType[], LookupType[]] = await Promise.all([
          fetchPublicLookups('sessionTypes'),
          fetchPublicLookups('modality'),
          fetchPublicLookups('availability'),
          fetchPublicLookups('experienceTraining'),
          fetchPublicLookups('language')
        ]);
        
        // Filter active lookups
        const activeSessionTypes = sessionTypeData.filter((lookup: LookupType) => lookup.active);
        const activeModalities = modalityData.filter((lookup: LookupType) => lookup.active);
        
        // Add 'All' option to session types if it doesn't exist
        const hasAllSessionType = activeSessionTypes.some(type => type.value === 'all');
        if (!hasAllSessionType) {
          activeSessionTypes.unshift({
            _id: 'all-session-type',
            value: 'all',
            displayName: 'All',
            active: true,
            sortOrder: -1,
            category: 'sessionTypes'
          });
        }
        
        // Add 'All' option to modalities if it doesn't exist
        const hasAllModality = activeModalities.some(type => type.value === 'all');
        if (!hasAllModality) {
          activeModalities.unshift({
            _id: 'all-modality',
            value: 'all',
            displayName: 'All',
            active: true,
            sortOrder: -1,
            category: 'modality'
          });
        }
        
        // Filter active availabilities and add 'Any' option if it doesn't exist
        const activeAvailabilities = availabilityData.filter((lookup: LookupType) => lookup.active);
        const hasAnyAvailability = activeAvailabilities.some(type => type.value === 'any');
        if (!hasAnyAvailability) {
          activeAvailabilities.unshift({
            _id: 'any-availability',
            value: 'any',
            displayName: 'Any',
            active: true,
            sortOrder: -1,
            category: 'availability'
          });
        }
        
        setSessionTypes(activeSessionTypes);
        setModalities(activeModalities);
        setAvailabilities(activeAvailabilities);
        setExperienceTrainingOptions(experienceTrainingData.filter((lookup: LookupType) => lookup.active));
        setLanguages(languageData.filter((lookup: LookupType) => lookup.active));
      } catch (error) {
        console.error('Error loading lookups:', error);
      }
    };
    loadLookups();
  }, []);

  useEffect(() => {
    const refreshCvUrl = async () => {
      if (formData.cv?.fileName) {
        try {
          const newUrl = await refreshFileUrl(formData.cv.fileName, token);
          setCvUrl(newUrl);
        } catch (error) {
          console.error('Error refreshing CV URL:', error);
          setUploadError('Failed to access CV file');
        }
      }
    };

    refreshCvUrl();
    // Refresh URL every 45 minutes to prevent expiration
    const refreshInterval = setInterval(refreshCvUrl, 45 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [formData.cv, token]);

  const handleMultiSelectChange = (field: string) => (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    
    // Handle 'All' option for session types
    if (field === 'sessionTypes') {
      const allOption = sessionTypes.find(type => type.value === 'all')?.value;
      
      // If 'All' is being selected, make it the only selection and close dropdown
      if (allOption && selectedValues.includes(allOption)) {
        onChange(field, [allOption]);
        setSessionTypesOpen(false);
        return;
      }
    }
    
    // Handle 'All' option for modalities
    if (field === 'modality') {
      const allOption = modalities.find(type => type.value === 'all')?.value;
      
      // If 'All' is being selected, make it the only selection and close dropdown
      if (allOption && selectedValues.includes(allOption)) {
        onChange(field, [allOption]);
        setModalityOpen(false);
        return;
      }
    }
    
    // Handle 'Any' option for availability
    if (field === 'availability') {
      const anyOption = availabilities.find(type => type.value === 'any')?.value;
      
      // If 'Any' is being selected, make it the only selection and close dropdown
      if (anyOption && selectedValues.includes(anyOption)) {
        onChange(field, [anyOption]);
        setAvailabilityOpen(false);
        return;
      }
    }
    
    onChange(field, selectedValues);
  };

  // State to track field-specific errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleFieldChange = (field: string, value: any) => {
    try {
      // Validate start date is in the future
      if (field === 'startDate' && value) {
        const startDate = dayjs(value);
        if (startDate.isValid() && startDate.isBefore(dayjs(), 'day')) {
          setFieldErrors(prev => ({
            ...prev,
            startDate: 'Preferred start date must be in the future'
          }));
          return; // Don't update the field if validation fails
        } else {
          // Clear error if validation passes
          setFieldErrors(prev => ({
            ...prev,
            startDate: ''
          }));
        }
      }
      
      // Validate years of experience
      if (field === 'qualifiedYears' && value) {
        const years = parseInt(value, 10);
        if (isNaN(years) || years < 0) {
          setFieldErrors(prev => ({
            ...prev,
            qualifiedYears: 'Please enter a valid number of years'
          }));
          return;
        } else if (years > 50) {
          setFieldErrors(prev => ({
            ...prev,
            qualifiedYears: 'Years of experience cannot exceed 50 years'
          }));
          return;
        } else {
          // Clear error if validation passes
          setFieldErrors(prev => ({
            ...prev,
            qualifiedYears: ''
          }));
        }
      }
      
      onChange(field, value);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setUploadError(errorMessage);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Only PDF and Word documents are allowed');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const response = await uploadFile(file, { applicationToken: token });
      onChange('cv', response);
      setCvUrl(response.fileUrl);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteCV = async () => {
    if (!formData.cv?.fileName) return;

    try {
      await deleteFile(formData.cv.fileName, token);
      onChange('cv', null);
      setCvUrl(null);
    } catch (error) {
      console.error('Delete error:', error);
      setUploadError('Failed to delete file. Please try again.');
    }
  };

  return (
    <Box ref={ref}>
      <Box component="form" sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Qualification Status
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                name="qualificationStatus"
                value={formData.qualified === true ? 'qualified' : (formData.qualified === false ? 'inTraining' : '')}
                onChange={(e) => {
                  const isQualified = e.target.value === 'qualified';
                  handleFieldChange('qualified', isQualified);
                  setQualificationSelected(true);
                }}
              >
                <FormControlLabel value="inTraining" control={<Radio />} label="In Training" />
                <FormControlLabel value="qualified" control={<Radio />} label="Qualified" />
              </RadioGroup>
            </FormControl>
            
            {formData.qualified === true && (
              <Box sx={{ mt: 2, pl: 2, borderLeft: '1px solid #e0e0e0' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Years of Experience"
                      type="number"
                      inputProps={{ min: 0, max: 50 }}
                      value={formData.qualifiedYears || ''}
                      onChange={(e) => handleFieldChange('qualifiedYears', e.target.value)}
                      required
                      error={!!fieldErrors.qualifiedYears}
                      helperText={fieldErrors.qualifiedYears || "Number of years as a qualified counsellor (max 50)"}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Client Hours"
                      value={formData.clientHours || ''}
                      onChange={(e) => handleFieldChange('clientHours', e.target.value)}
                      required
                      helperText="Total client hours completed"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Supervised Hours"
                      value={formData.supervisedHours || ''}
                      onChange={(e) => handleFieldChange('supervisedHours', e.target.value)}
                      required
                      helperText="Total supervised hours completed"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Grid>

          {formData.qualified !== undefined && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Counselling Preferences
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="sessionTypes-label">Session Types</InputLabel>
                  <Select
                    labelId="sessionTypes-label"
                    id="sessionTypes"
                    multiple
                    value={Array.isArray(formData.sessionTypes) ? formData.sessionTypes : []}
                    onChange={handleMultiSelectChange('sessionTypes')}
                    open={sessionTypesOpen}
                    onOpen={() => setSessionTypesOpen(true)}
                    onClose={() => setSessionTypesOpen(false)}
                    input={<OutlinedInput label="Session Types" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value: string) => {
                          const lookup = sessionTypes.find((type: LookupType) => type.value === value);
                          return (
                            <Chip 
                              key={value} 
                              label={lookup?.displayName || value} 
                              onDelete={(e) => {
                                e.stopPropagation();
                                const newSelected = formData.sessionTypes?.filter(v => v !== value) || [];
                                handleFieldChange('sessionTypes', newSelected);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {sessionTypes.map((type: LookupType) => (
                      <MenuItem key={type._id} value={type.value}>
                        {type.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="modality-label">Modalities</InputLabel>
                  <Select
                    labelId="modality-label"
                    id="modality"
                    multiple
                    value={Array.isArray(formData.modality) ? formData.modality : []}
                    onChange={handleMultiSelectChange('modality')}
                    open={modalityOpen}
                    onOpen={() => setModalityOpen(true)}
                    onClose={() => setModalityOpen(false)}
                    input={<OutlinedInput label="Modalities" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value: string) => {
                          const lookup = modalities.find((type: LookupType) => type.value === value);
                          return (
                            <Chip 
                              key={value} 
                              label={lookup?.displayName || value} 
                              onDelete={(e) => {
                                e.stopPropagation();
                                const newSelected = formData.modality?.filter(v => v !== value) || [];
                                handleFieldChange('modality', newSelected);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {modalities.map((type: LookupType) => (
                      <MenuItem key={type._id} value={type.value}>
                        {type.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Availability Time Slots */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                  Availability Time Slots
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Specify exact days and times when you are available for sessions
                </Typography>
                <AvailabilitySlotEditor
                  slots={formData.availabilitySlots || []}
                  onChange={(slots: AvailabilitySlot[]) => handleFieldChange('availabilitySlots', slots)}
                />
              </Grid>

              {/* General Availability */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="availability-label">General Availability (Optional)</InputLabel>
                  <Select
                    labelId="availability-label"
                    id="availability"
                    multiple
                    value={Array.isArray(formData.availability) ? formData.availability : []}
                    onChange={handleMultiSelectChange('availability')}
                    open={availabilityOpen}
                    onOpen={() => setAvailabilityOpen(true)}
                    onClose={() => setAvailabilityOpen(false)}
                    input={<OutlinedInput label="General Availability (Optional)" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value: string) => {
                          const lookup = availabilities.find((type: LookupType) => type.value === value);
                          return (
                            <Chip 
                              key={value} 
                              label={lookup?.displayName || value} 
                              onDelete={(e) => {
                                e.stopPropagation();
                                const newSelected = formData.availability?.filter(v => v !== value) || [];
                                handleFieldChange('availability', newSelected);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {[...availabilities]
                      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                      .map((type: LookupType) => (
                        <MenuItem key={type._id} value={type.value}>
                          {type.displayName}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  General availability preferences - use time slots above for specific scheduling
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Experience
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="experienceOrTraining-label">Experience / Training</InputLabel>
                  <Select
                    labelId="experienceOrTraining-label"
                    id="experienceOrTraining"
                    multiple
                    value={Array.isArray(formData.experienceOrTraining) ? formData.experienceOrTraining : []}
                    onChange={handleMultiSelectChange('experienceOrTraining')}
                    input={<OutlinedInput label="Experience / Training" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value: string) => {
                          const lookup = experienceTrainingOptions.find((option: LookupType) => option.value === value);
                          return (
                            <Chip 
                              key={value} 
                              label={lookup?.displayName || value} 
                              onDelete={(e) => {
                                e.stopPropagation();
                                const newSelected = formData.experienceOrTraining?.filter(v => v !== value) || [];
                                handleFieldChange('experienceOrTraining', newSelected);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {experienceTrainingOptions.map((option: LookupType) => (
                      <MenuItem key={option._id} value={option.value}>
                        {option.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Experience"
                  multiline
                  minRows={4}
                  maxRows={12}
                  value={formData.experience || ''}
                  onChange={(e) => handleFieldChange('experience', e.target.value)}
                  helperText="Please use this opportunity to sell yourself to us so that we can match you with the most appropriate clients. Describe your current counselling experience and roles, or experience of working in similar fields such as: Ex-offenders service, Homeless support, Nursing, Teaching, Mental health support, Samaritans, etc."
                  sx={{ '& .MuiInputBase-root': { height: 'auto' } }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Previous Experience"
                  multiline
                  minRows={4}
                  maxRows={12}
                  value={formData.previousExperience || ''}
                  onChange={(e) => handleFieldChange('previousExperience', e.target.value)}
                  helperText="Please use this opportunity to sell yourself to us so that we can match you with the most appropriate clients. Describe any relevant previous experience in counselling or similar fields such as: Ex-offenders service, Homeless support, Nursing, Teaching, Mental health support, Samaritans, etc."
                  sx={{ '& .MuiInputBase-root': { height: 'auto' } }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Languages Spoken</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(formData.languages) ? formData.languages : []}
                    onChange={(e) => handleFieldChange('languages', e.target.value)}
                    input={<OutlinedInput label="Languages Spoken" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const lookup = languages.find(l => l.value === value);
                          return <Chip key={value} label={lookup?.displayName || value} size="small" />;
                        })}
                      </Box>
                    )}
                  >
                    {languages.map((lookup) => (
                      <MenuItem key={lookup._id} value={lookup.value}>
                        {lookup.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Additional Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Why are you interested in being a counsellor for us?"
                  multiline
                  rows={4}
                  value={formData.whyInterested || ''}
                  onChange={(e) => handleFieldChange('whyInterested', e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                  <DatePicker
                    label="Preferred Start Date"
                    value={formData.startDate ? dayjs(formData.startDate) : null}
                    onChange={(date) => handleFieldChange('startDate', date?.format('YYYY-MM-DD') || '')}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: { 
                        fullWidth: true,
                        error: !!fieldErrors.startDate,
                        helperText: fieldErrors.startDate || 'When would you like to start? (DD/MM/YYYY)'
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Upload your CV (should not exceed more than 2 pages and no more than 5MB)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {formData.cv ? (
                    <>
                      <Button
                        component="a"
                        href={cvUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        disabled={!cvUrl}
                      >
                        {formData.cv.originalName}
                      </Button>
                      <IconButton 
                        onClick={handleDeleteCV}
                        color="error"
                        disabled={uploading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  ) : (
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<UploadFileIcon />}
                      disabled={uploading}
                    >
                      Upload CV
                      <input
                        type="file"
                        hidden
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx"
                      />
                    </Button>
                  )}
                  {uploading && <CircularProgress size={24} />}
                </Box>
                {uploadError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {uploadError}
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button onClick={onBack} variant="outlined">
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onSave}
                  >
                    Save Progress
                  </Button>
                  <Button variant="contained" color="primary" onClick={onNext}>
                    Next
                  </Button>
                </Box>
              </Grid>

              {saveStatus === 'saved' && (
                <Grid item xs={12}>
                  <Alert severity="success">Progress saved successfully!</Alert>
                </Grid>
              )}
              
              {saveStatus === 'error' && (
                <Grid item xs={12}>
                  <Alert severity="error">Error saving progress. Please try again.</Alert>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
});

CounsellingInformationTab.displayName = 'CounsellingInformationTab';

export default CounsellingInformationTab;